const defaultLocale = 'it-IT';

/**
 * Una data che non c'è si scrive con una lineetta, non con «Invalid Date».
 *
 * `new Date('')` non lancia: produce una data non valida, e `toLocaleDateString` la stampa come
 * «Invalid Date» — in italiano, in mezzo a una tabella. Il difetto non si vedeva perché i dati di
 * prova avevano sempre la data; è saltato fuori con una pratica vera in cui la data non è mai stata
 * detta, che è il caso normale di una richiesta appena arrivata.
 *
 * Il controllo sta QUI e non nei sedici punti che chiamano: una data mancante è una condizione del
 * dato, non di una schermata, e chiedere a ogni chiamante di ricordarsene è il modo di riavere lo
 * stesso difetto altrove.
 */
const EMPTY = '—';

function invalid(isoDate: string): boolean {
  return !isoDate || Number.isNaN(new Date(isoDate).getTime());
}

export function formatDate(isoDate: string, locale = defaultLocale): string {
  if (invalid(isoDate)) return EMPTY;

  return new Date(isoDate).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Data e ora leggibili. I secondi si chiedono con `withSeconds`, e restano fuori per default: su
 * una data di creazione o di scadenza non dicono nulla, mentre servono dove due eventi vicini
 * vanno distinti — il log delle esecuzioni di un'automazione, dove un salvataggio può produrre più
 * passate nello stesso minuto.
 */
export function formatDateTime(
  isoDate: string,
  locale = defaultLocale,
  withSeconds = false,
): string {
  if (invalid(isoDate)) return EMPTY;

  return new Date(isoDate).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...(withSeconds ? { second: '2-digit' } : {}),
  });
}

/**
 * Quanto tempo fa, o fra quanto: «3 giorni fa», «tra 2 ore».
 *
 * Anche qui una data che non c'è vale una lineetta, e qui conta di più: `Intl.RelativeTimeFormat`
 * su `NaN` non stampa «Invalid Date» ma lancia un `RangeError`, che fa cadere l'intera pagina. È
 * successo in produzione con i messaggi importati senza data di ricezione.
 */
export function formatRelativeDate(isoDate: string, locale = defaultLocale): string {
  if (invalid(isoDate)) return EMPTY;

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const diff = new Date(isoDate).getTime() - Date.now();
  const absDiff = Math.abs(diff);

  if (absDiff < 60_000) return rtf.format(Math.round(diff / 1000), 'second');
  if (absDiff < 3_600_000) return rtf.format(Math.round(diff / 60_000), 'minute');
  if (absDiff < 86_400_000) return rtf.format(Math.round(diff / 3_600_000), 'hour');
  return rtf.format(Math.round(diff / 86_400_000), 'day');
}

/**
 * Durata leggibile da millisecondi: sotto il minuto, secondi con 1 decimale
 * (es. "0,7 s"); da un minuto in su, "Xh Ym Zs" (es. 7320 s -> "2h 2m 0s", ore
 * omesse se 0). Evita valori poco leggibili tipo "7320,0 s".
 *
 * NON esportata: è la coda lunga di {@link formatNanos}, che è l'unico modo in cui il resto
 * dell'applicazione formatta una durata. Due funzioni simili nello stesso namespace sarebbero un
 * invito a chiamare quella sbagliata — e passare nanosecondi a questa darebbe un numero un milione
 * di volte più grande, in silenzio. Se un giorno servisse formattare millisecondi che non vengono
 * dalle automazioni, questo è il punto da riesportare, consapevolmente.
 */
function formatDuration(ms: number, locale = defaultLocale): string {
  const totalSeconds = ms / 1000;
  if (totalSeconds < 60) {
    return `${totalSeconds.toLocaleString(locale, { maximumFractionDigits: 1 })} s`;
  }
  const total = Math.round(totalSeconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`;
}

/**
 * Durata leggibile da NANOSECONDI, resa nell'unità che compete al suo ordine di grandezza: dai
 * nanosecondi di una singola valutazione ai secondi di un run vero.
 *
 * È l'unico formattatore da usare sulle durate delle AUTOMAZIONI, dove l'unità è una sola —
 * dalla singola condizione al run intero — e i valori vanno da qualche centinaio di nanosecondi a
 * diversi secondi. {@link formatDuration} da solo non basta: è tarato sui millisecondi e scende al
 * decimo di SECONDO, quindi su un dry-run direbbe sempre "0 s". Gli si delega la sola coda lunga,
 * dal secondo in su, dove la sua resa è quella giusta e l'utente la riconosce.
 *
 * Sta qui e non in una schermata perché i consumatori sono due — simulazione e monitoraggio — e
 * l'unità è una convenzione di dominio, non una scelta di pagina.
 */
export function formatNanos(nanos: number, locale = defaultLocale): string {
  if (nanos < 1_000) return `${nanos.toLocaleString(locale)} ns`;

  if (nanos < 1_000_000)
    return `${(nanos / 1_000).toLocaleString(locale, { maximumFractionDigits: 1 })} µs`;

  if (nanos < 1_000_000_000)
    return `${(nanos / 1_000_000).toLocaleString(locale, { maximumFractionDigits: 1 })} ms`;

  return formatDuration(nanos / 1_000_000, locale);
}

// ─── Conversione datetime-local <-> ISO backend ────────────────────────────
//
// Convenzione UNICA del progetto: il backend memorizza/ritorna ISTANTI in UTC
// ("YYYY-MM-DDTHH:mm:ssZ"); il client mostra ed edita in ORA LOCALE del browser.
// Questi due helper sono l'unico punto di conversione client<->server: usarli
// ovunque (input `datetime-local`) invece di concatenare ":00Z" o fare
// substring/string-slice — che estrarrebbero i componenti UTC sbagliando l'orario
// di N ore (offset del fuso). Per la sola VISUALIZZAZIONE usare `formatDateTime`.

const pad2 = (n: number) => String(n).padStart(2, '0');

/**
 * Valore di un input `datetime-local` ("YYYY-MM-DDTHH:mm", interpretato nel fuso
 * LOCALE) -> ISO UTC per il backend ("YYYY-MM-DDTHH:mm:ssZ", senza millisecondi).
 * null/'' o data non valida -> null. Inverso di {@link isoToDatetimeLocal}.
 */
export function datetimeLocalToIso(local: string | null | undefined): string | null {
  if (!local) return null;
  const date = new Date(local);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.toISOString().slice(0, 19)}Z`;
}

/**
 * ISO UTC del backend -> valore per un input `datetime-local` ("YYYY-MM-DDTHH:mm")
 * nel fuso LOCALE del browser. null/non valido -> ''. Inverso di
 * {@link datetimeLocalToIso}: usa `new Date()` (componenti locali), non lo slice
 * della stringa, cosi' l'orario mostrato e' coerente con il display locale.
 */
export function isoToDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(
    date.getHours(),
  )}:${pad2(date.getMinutes())}`;
}

/** Ora corrente (LOCALE) nel formato di un input `datetime-local` ("YYYY-MM-DDTHH:mm"). */
export function nowDatetimeLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(
    d.getHours(),
  )}:${pad2(d.getMinutes())}`;
}
